import { FormEvent, useEffect, useMemo, useState } from "react";
import { formatExpirationMonth } from "../lib/expiration";
import { REMOVE_REASON_OPTIONS, type RemoveReason } from "../lib/removeReasons";
import type { StockLotChoice } from "../lib/types";
import { ExpirationMonthInput } from "./ExpirationMonthInput";
import { ModalCloseButton } from "./ModalCloseButton";

export type SourceType = "ambulance" | "hospital" | "other" | "stock";

export interface QuickEditValue {
  enteredName: string;
  quantity: number;
  expirationDate: string;
  notes: string;
  sourceType?: SourceType | "";
  stockLotId?: string;
  stockTransferQuantity?: number;
  actionReason?: RemoveReason | "";
  removeReason?: RemoveReason | "";
}

function sanitizeWholeNumberInput(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  return String(Number(digits));
}

function parseWholeNumberInput(raw: string): number {
  if (!raw) return 0;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.round(parsed));
}

function normalizeOptionalText(value: string | null | undefined): string {
  if (!value) return "";
  return value.trim();
}

export function splitWillUpdateExistingLine(
  currentExpiration: string | null | undefined,
  targetExpiration: string | null | undefined
): boolean {
  return normalizeOptionalText(currentExpiration) === normalizeOptionalText(targetExpiration);
}

function splitMergeWarningText(currentExpiration: string | null | undefined): string {
  if (!normalizeOptionalText(currentExpiration)) {
    return "Saving without an expiration month will update the existing line quantity instead of adding a separate line.";
  }
  return "Saving with the same expiration month will update the existing line quantity instead of adding a separate line.";
}

export function isStockSourceDisabled(
  lotsLoading: boolean,
  lots: StockLotChoice[]
): boolean {
  if (lotsLoading) return true;
  return !lots.some((lot) => !lot.isBackorder && lot.quantity > 0);
}

export function shouldShowQuickEditNameField(removeMode: boolean, actionReasonRequired: boolean): boolean {
  return !(removeMode && actionReasonRequired);
}

export function QuickEditSheet({
  open,
  resetKey,
  title,
  initial,
  presentation,
  showMove,
  showNotes,
  sourceMode,
  allowedSourceTypes,
  allowNoChangeSave,
  disableSourceUntilChange,
  verificationPrompt,
  submitLabel,
  stockLots,
  stockLotsLoading,
  requireActionReason,
  actionReasonOptions,
  requireRemoveReason,
  removeReasonOptions,
  onClose,
  onSave,
  onAddExpiration,
  onRemove,
  onMove
}: {
  open: boolean;
  resetKey?: string;
  title: string;
  initial: QuickEditValue;
  presentation?: "sheet" | "modal";
  showMove?: boolean;
  showNotes?: boolean;
  sourceMode?: "none" | "apparatus";
  allowedSourceTypes?: SourceType[];
  allowNoChangeSave?: boolean;
  disableSourceUntilChange?: boolean;
  verificationPrompt?: string | null;
  submitLabel?: string;
  stockLots?: StockLotChoice[];
  stockLotsLoading?: boolean;
  requireActionReason?: boolean;
  actionReasonOptions?: typeof REMOVE_REASON_OPTIONS;
  requireRemoveReason?: boolean;
  removeReasonOptions?: typeof REMOVE_REASON_OPTIONS;
  isExpiringItem?: boolean;
  onClose: () => void;
  onSave: (value: QuickEditValue) => Promise<void>;
  onAddExpiration?: (value: QuickEditValue) => Promise<void>;
  onRemove?: (value: QuickEditValue) => Promise<void>;
  onMove?: (qty: number, value: QuickEditValue) => Promise<void>;
}) {
  const [value, setValue] = useState<QuickEditValue>(initial);
  const [quantityInput, setQuantityInput] = useState(initial.quantity <= 0 ? "" : String(initial.quantity));
  const [stockTransferInput, setStockTransferInput] = useState(String(initial.stockTransferQuantity ?? 1));
  const [splitMode, setSplitMode] = useState(false);
  const [actionReason, setActionReason] = useState<RemoveReason | "">(initial.actionReason ?? "");
  const [removeMode, setRemoveMode] = useState(false);
  const [removeReason, setRemoveReason] = useState<RemoveReason | "">("");
  const [splitQuantityInput, setSplitQuantityInput] = useState("");
  const [splitExpirationDate, setSplitExpirationDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const useModal = presentation === "modal";
  const notesVisible = showNotes ?? true;
  const currentMode = sourceMode ?? "none";
  const sourceTypes = allowedSourceTypes ?? ["ambulance", "hospital", "stock", "other"];
  const allowSaveWithoutChanges = Boolean(allowNoChangeSave);
  const sourceRequiresItemChange = Boolean(disableSourceUntilChange);
  const actionReasonRequired = Boolean(requireActionReason);
  const removeReasonRequired = requireRemoveReason ?? !actionReasonRequired;
  const actionReasonChoices = actionReasonOptions ?? REMOVE_REASON_OPTIONS;
  const removeReasonChoices = removeReasonOptions ?? REMOVE_REASON_OPTIONS;
  const lots = stockLots ?? [];
  const lotsLoading = Boolean(stockLotsLoading);

  useEffect(() => {
    if (!open) return;
    setValue({
      ...initial,
      quantity: initial.quantity <= 0 ? 0 : initial.quantity,
      expirationDate: initial.quantity <= 0 ? "" : initial.expirationDate,
      sourceType: "",
      stockLotId: ""
    });
    setQuantityInput(initial.quantity <= 0 ? "" : String(initial.quantity));
    setStockTransferInput(String(initial.stockTransferQuantity ?? 1));
    setSplitMode(false);
    setActionReason(initial.actionReason ?? "");
    setRemoveMode(false);
    setRemoveReason("");
    setSplitQuantityInput("");
    setSplitExpirationDate("");
    setFormError("");
  }, [open, resetKey]);

  const quantityValue = parseWholeNumberInput(quantityInput);
  const initialQuantity = Math.max(0, Math.round(initial.quantity));
  const normalizedInitialExpiration = initial.quantity <= 0 ? "" : initial.expirationDate || "";
  const normalizedNewExpiration = quantityValue <= 0 ? "" : value.expirationDate || "";
  const normalizedInitialNotes = normalizeOptionalText(initial.notes);
  const normalizedNewNotes = normalizeOptionalText(value.notes);
  const quantityChanged = quantityValue !== initialQuantity;
  const expirationChanged = normalizedInitialExpiration !== normalizedNewExpiration;
  const notesChanged = normalizedInitialNotes !== normalizedNewNotes;
  const hasNonStockChanges = quantityChanged || expirationChanged || notesChanged;
  const zeroStillZero = initialQuantity === 0 && quantityValue === 0;
  const quantityIncreased = quantityValue > initial.quantity;
  const quantityDecreased = quantityValue < initial.quantity;
  const sourceRequired = currentMode === "apparatus" && !quantityDecreased && (quantityIncreased || expirationChanged);
  const sourceSelectEnabled = !sourceRequiresItemChange || splitMode || quantityChanged || expirationChanged;
  const stockSourceDisabled = isStockSourceDisabled(lotsLoading, lots);
  const effectiveSourceType =
    !sourceSelectEnabled
      ? ""
      : value.sourceType && !sourceTypes.includes(value.sourceType)
      ? ""
      : stockSourceDisabled && value.sourceType === "stock"
        ? ""
        : value.sourceType;
  const stockMode = currentMode === "apparatus" && effectiveSourceType === "stock";
  const canSplit = Boolean(onAddExpiration) && currentMode === "apparatus" && initialQuantity > 0;
  const splitQuantityValue = parseWholeNumberInput(splitQuantityInput);
  const sourceMarkedRequired = sourceRequired || stockMode || splitMode;
  const primarySubmitLabel = stockMode ? "Move From Stock" : submitLabel ?? (currentMode === "apparatus" ? "Replace" : "Save");

  const selectedStockItem = useMemo(
    () => lots.find((lot) => lot.stockLotId === value.stockLotId) ?? null,
    [lots, value.stockLotId]
  );
  const manualSplitWillUpdateExistingLine =
    splitMode && !stockMode && splitWillUpdateExistingLine(initial.expirationDate, splitExpirationDate);
  const stockSplitWillUpdateExistingLine =
    splitMode &&
    stockMode &&
    Boolean(selectedStockItem) &&
    splitWillUpdateExistingLine(initial.expirationDate, selectedStockItem?.expirationDate ?? "");

  const stockTransferQuantity = Math.max(0, parseWholeNumberInput(stockTransferInput));
  if (!open) return null;

  const startSplit = () => {
    setFormError("");
    setSplitMode(true);
    setRemoveMode(false);
    setSplitQuantityInput("");
    setSplitExpirationDate("");
    setValue((prev) => ({
      ...prev,
      sourceType: stockSourceDisabled && prev.sourceType === "stock" ? "" : prev.sourceType,
      stockLotId: ""
    }));
  };

  const cancelSplit = () => {
    setFormError("");
    setSplitMode(false);
    setSplitQuantityInput("");
    setSplitExpirationDate("");
  };

  const startRemove = () => {
    setFormError("");
    if (actionReasonRequired && !actionReason) {
      setFormError("Reason for change is required before removing.");
      return;
    }
    setSplitMode(false);
    setRemoveMode(true);
    setRemoveReason("");
  };

  const cancelRemove = () => {
    setFormError("");
    setRemoveMode(false);
    setRemoveReason("");
  };

  const validateStockSelection = () => {
    if (lotsLoading) {
      setFormError("Stock items are still loading.");
      return false;
    }
    if (!selectedStockItem) {
      setFormError("Choose a stock item.");
      return false;
    }
    if (selectedStockItem.isBackorder) {
      setFormError("Backorder items cannot be moved to apparatus.");
      return false;
    }
    if (stockTransferQuantity <= 0) {
      setFormError("Quantity from stock must be at least 1.");
      return false;
    }
    if (stockTransferQuantity > selectedStockItem.quantity) {
      setFormError("Quantity exceeds available stock.");
      return false;
    }
    return true;
  };

  const validateActionReason = () => {
    if (!actionReasonRequired || actionReason) return true;
    setFormError("Reason is required.");
    return false;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError("");

    if (removeMode) {
      await remove();
      return;
    }

    if (splitMode) {
      await addExpiration();
      return;
    }

    const enteredName = value.enteredName.trim();

    if (!enteredName) {
      setFormError("Your Name is required.");
      return;
    }
    if (!validateActionReason()) {
      return;
    }

    if (sourceRequired && !value.sourceType) {
      setFormError("Source is required when increasing quantity or changing expiration.");
      return;
    }

    if (!allowSaveWithoutChanges && !stockMode && zeroStillZero) {
      setFormError("Quantity is still 0. Increase quantity or cancel.");
      return;
    }

    if (!allowSaveWithoutChanges && !stockMode && !hasNonStockChanges) {
      setFormError("No changes to save.");
      return;
    }

    if (stockMode && !validateStockSelection()) {
      return;
    }

    setBusy(true);
    try {
      await onSave({
        ...value,
        enteredName,
        quantity: quantityValue,
        expirationDate: normalizedNewExpiration,
        sourceType: stockMode ? "stock" : sourceRequired ? value.sourceType : undefined,
        stockTransferQuantity: stockMode ? stockTransferQuantity : undefined,
        actionReason: actionReasonRequired ? actionReason : undefined
      });
      onClose();
    } catch (err: any) {
      setFormError(err?.message ?? "Unable to save changes");
    } finally {
      setBusy(false);
    }
  };

  const addExpiration = async () => {
    if (!onAddExpiration) return;
    setFormError("");
    const enteredName = value.enteredName.trim();

    if (!enteredName) {
      setFormError("Your Name is required.");
      return;
    }
    if (!validateActionReason()) {
      return;
    }
    if (!effectiveSourceType) {
      setFormError("Source is required when splitting this item.");
      return;
    }
    if (stockMode && !validateStockSelection()) {
      return;
    }
    if (!stockMode && splitQuantityValue <= 0) {
      setFormError("Quantity must be at least 1.");
      return;
    }
    setBusy(true);
    try {
      await onAddExpiration({
        ...value,
        enteredName,
        quantity: stockMode ? stockTransferQuantity : splitQuantityValue,
        expirationDate: stockMode ? selectedStockItem?.expirationDate ?? "" : splitExpirationDate,
        notes: notesVisible ? value.notes : "",
        sourceType: effectiveSourceType as SourceType,
        stockTransferQuantity: stockMode ? stockTransferQuantity : undefined,
        actionReason: actionReasonRequired ? actionReason : undefined
      });
      onClose();
    } catch (err: any) {
      setFormError(err?.message ?? "Unable to split item");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onRemove) return;
    const enteredName = value.enteredName.trim();
    if (!enteredName) {
      setFormError("Your Name is required.");
      return;
    }
    if (!validateActionReason()) {
      return;
    }
    if (removeReasonRequired && !removeReason) {
      setFormError("Choose why this item is being removed.");
      return;
    }
    setBusy(true);
    try {
      await onRemove({
        ...value,
        enteredName,
        actionReason: actionReasonRequired ? actionReason : undefined,
        removeReason: removeReasonRequired ? removeReason : undefined
      });
      onClose();
    } catch (err: any) {
      setFormError(err?.message ?? "Unable to remove line item");
    } finally {
      setBusy(false);
    }
  };

  const move = async () => {
    if (!onMove) return;
    const enteredName = value.enteredName.trim();
    if (!enteredName) {
      setFormError("Your Name is required.");
      return;
    }
    setBusy(true);
    try {
      await onMove(value.quantity, { ...value, enteredName });
      onClose();
    } catch (err: any) {
      setFormError(err?.message ?? "Unable to move item");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={useModal ? "modal-backdrop" : "sheet-backdrop"} onClick={() => (busy ? undefined : onClose())}>
      <div className={useModal ? "modal-card quick-edit-modal" : "sheet-card"} onClick={(event) => event.stopPropagation()}>
        {!useModal ? <div className="sheet-handle" /> : null}
        <ModalCloseButton onClick={onClose} disabled={busy} />
        <h3>{title}</h3>
        {verificationPrompt ? (
          <div className="card-soft stack-xs">
            <span className="reason-label">Verification note</span>
            <p className="small">{verificationPrompt}</p>
          </div>
        ) : null}
        <form onSubmit={submit} className="stack-sm">
          {shouldShowQuickEditNameField(removeMode, actionReasonRequired) ? (
            <label className="stack-xs">
              <span className="label">Your Name</span>
              <input
                className="input"
                value={value.enteredName}
                onChange={(event) => setValue((prev) => ({ ...prev, enteredName: event.target.value }))}
                required
              />
            </label>
          ) : null}

          {actionReasonRequired && !removeMode ? (
            <label className="stack-xs">
              <span className="label">Reason for change *</span>
              <select
                className="input"
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value as RemoveReason | "")}
                required
              >
                <option value="">Select reason</option>
                {actionReasonChoices.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {removeMode ? (
            <div className="card-soft stack-xs">
              <p className="muted small">
                {removeReasonRequired
                  ? "Remove sets this item line to 0 and clears its expiration. Choose the reason so the log explains what happened."
                  : "Remove sets this item line to 0 and clears its expiration."}
              </p>
              {removeReasonRequired ? (
                <label className="stack-xs">
                  <span className="label">Remove Reason *</span>
                  <select
                    className="input"
                    value={removeReason}
                    onChange={(event) => setRemoveReason(event.target.value as RemoveReason | "")}
                    required
                  >
                    <option value="">Select reason</option>
                    {removeReasonChoices.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : currentMode === "apparatus" ? (
            <label className="stack-xs">
              <span className="label">Source{sourceMarkedRequired ? " *" : ""}</span>
              <select
                className="input"
                value={effectiveSourceType ?? ""}
                disabled={!sourceSelectEnabled}
                onChange={(event) => {
                  const nextSource = event.target.value as SourceType | "";
                  if (nextSource === "stock" && stockSourceDisabled) return;
                  setValue((prev) => ({
                    ...prev,
                    sourceType: nextSource,
                    stockLotId: ""
                  }));
                }}
              >
                <option value="">Select source</option>
                {sourceTypes.includes("ambulance") ? <option value="ambulance">Ambulance</option> : null}
                {sourceTypes.includes("hospital") ? <option value="hospital">Hospital</option> : null}
                {sourceTypes.includes("stock") ? <option value="stock" disabled={stockSourceDisabled}>Stock</option> : null}
                {sourceTypes.includes("other") ? <option value="other">Other</option> : null}
              </select>
            </label>
          ) : null}

          {!removeMode && stockMode ? (
            <div className="card-soft stack-xs">
              <p className="muted small">
                {splitMode
                  ? "Move stock into this kit as a separate item line. Stock quantity will be decremented automatically."
                  : "Move stock into this kit item. Stock quantity will be decremented automatically."}
              </p>
              <label className="stack-xs">
                <span className="label">Stock item *</span>
                <select
                  className="input"
                  value={value.stockLotId ?? ""}
                  onChange={(event) => setValue((prev) => ({ ...prev, stockLotId: event.target.value }))}
                  disabled={lotsLoading}
                >
                  <option value="">Select stock item</option>
                  {lots.map((lot) => {
                    const parts = [
                      lot.binLabel ? `Bin ${lot.binLabel}` : "No bin",
                      lot.expirationDate ? `Exp ${formatExpirationMonth(lot.expirationDate)}` : "No exp",
                      `Available ${lot.quantity}`
                    ];
                    const suffix = lot.isBackorder ? " • Backorder" : "";
                    return (
                      <option key={lot.stockLotId} value={lot.stockLotId} disabled={lot.isBackorder}>
                        {`${parts.join(" • ")}${suffix}`}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="stack-xs">
                <span className="label">Quantity from stock</span>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={stockTransferInput}
                  onFocus={(event) => event.currentTarget.select()}
                  onChange={(event) => {
                    const cleaned = sanitizeWholeNumberInput(event.target.value);
                    const nextValue = parseWholeNumberInput(cleaned);
                    setValue((prev) => ({
                      ...prev,
                      stockTransferQuantity: nextValue
                    }));
                    setStockTransferInput(cleaned);
                  }}
                  onBlur={() => {
                    if (!stockTransferInput) {
                      setStockTransferInput("1");
                      setValue((prev) => ({ ...prev, stockTransferQuantity: 1 }));
                    }
                  }}
                />
              </label>

              {selectedStockItem ? (
                <p className="muted small">
                  Available {selectedStockItem.quantity}
                  {selectedStockItem.expirationDate ? ` • Exp ${formatExpirationMonth(selectedStockItem.expirationDate)}` : ""}
                  {selectedStockItem.notes ? ` • ${selectedStockItem.notes}` : ""}
                </p>
              ) : null}

              {stockSplitWillUpdateExistingLine ? (
                <p className="warning-text small">{splitMergeWarningText(initial.expirationDate)}</p>
              ) : null}
            </div>
          ) : !removeMode && splitMode ? (
            <div className="card-soft stack-xs">
              <p className="muted small">Add a separate line for this item. The existing line will stay as-is.</p>
              <label className="stack-xs">
                <span className="label">Split Quantity</span>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={splitQuantityInput}
                  onFocus={(event) => event.currentTarget.select()}
                  onChange={(event) => setSplitQuantityInput(sanitizeWholeNumberInput(event.target.value))}
                />
              </label>

              <ExpirationMonthInput
                label="Expiration"
                value={splitExpirationDate}
                onChange={setSplitExpirationDate}
                allowEmpty
              />

              {manualSplitWillUpdateExistingLine ? (
                <p className="warning-text small">{splitMergeWarningText(initial.expirationDate)}</p>
              ) : null}
            </div>
          ) : !removeMode ? (
            <>
              <label className="stack-xs">
                <span className="label">Quantity</span>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={quantityInput}
                  onFocus={(event) => event.currentTarget.select()}
                  onChange={(event) => {
                    const cleaned = sanitizeWholeNumberInput(event.target.value);
                    const nextQuantity = parseWholeNumberInput(cleaned);
                    setQuantityInput(cleaned);
                    setValue((prev) => ({
                      ...prev,
                      quantity: nextQuantity,
                      expirationDate: nextQuantity <= 0 ? "" : prev.expirationDate
                    }));
                  }}
                  onBlur={() => {
                    if (!quantityInput) {
                      setQuantityInput("0");
                      setValue((prev) => ({ ...prev, quantity: 0, expirationDate: "" }));
                    }
                  }}
                />
              </label>

              <ExpirationMonthInput
                label="Expiration"
                value={value.expirationDate}
                onChange={(expirationDate) => setValue((prev) => ({ ...prev, expirationDate }))}
                allowEmpty
              />
            </>
          ) : null}

          {!removeMode && notesVisible ? (
            <label className="stack-xs">
              <span className="label">Notes</span>
              <textarea
                className="input"
                rows={2}
                placeholder="eg: backorder"
                value={value.notes}
                onChange={(event) => setValue((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </label>
          ) : null}

          {formError ? <p className="error-text small">{formError}</p> : null}

          <div className="row-between gap-sm">
            <div className="actions-wrap">
              {removeMode ? (
                <button type="button" className="btn btn-secondary" onClick={cancelRemove} disabled={busy}>
                  Cancel Remove
                </button>
              ) : splitMode ? (
                <button type="button" className="btn btn-secondary" onClick={cancelSplit} disabled={busy}>
                  Cancel Split
                </button>
              ) : onRemove ? (
                <button type="button" className="btn btn-danger" onClick={startRemove} disabled={busy}>
                  Remove
                </button>
              ) : null}
              {!removeMode && showMove && onMove ? (
                <button type="button" className="btn btn-secondary" onClick={move} disabled={busy}>
                  Move
                </button>
              ) : null}
            </div>
            <div className="actions-wrap">
              {removeMode ? (
                <button type="button" className="btn btn-danger" onClick={remove} disabled={busy}>
                  {busy ? "Removing..." : "Confirm Remove"}
                </button>
              ) : splitMode ? (
                <button type="button" className="btn btn-primary" onClick={addExpiration} disabled={busy}>
                  {busy ? "Saving..." : "Save Split"}
                </button>
              ) : canSplit ? (
                <button type="button" className="btn btn-secondary" onClick={startSplit} disabled={busy}>
                  Split
                </button>
              ) : null}
              {!removeMode && !splitMode ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={busy || (!allowSaveWithoutChanges && !stockMode && (zeroStillZero || !hasNonStockChanges))}
                >
                  {busy ? "Saving..." : primarySubmitLabel}
                </button>
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
